import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';

export default function ResetPassword() {
    const navigate = useNavigate();
    const location = useLocation();
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
    // Get mobile number from navigation state
    const mobile = location.state?.mobile || '';

    // Password strength indicator
    const getPasswordStrength = (password) => {
        if (!password) return { strength: 0, label: '', color: '' };
        
        let strength = 0;
        if (password.length >= 6) strength += 1;
        if (/[a-z]/.test(password)) strength += 1;
        if (/[A-Z]/.test(password)) strength += 1;
        if (/[0-9]/.test(password)) strength += 1;
        if (/[^A-Za-z0-9]/.test(password)) strength += 1;

        const strengths = [
            { label: 'Very Weak', color: '#dc3545' },
            { label: 'Weak', color: '#fd7e14' },
            { label: 'Fair', color: '#ffc107' },
            { label: 'Good', color: '#20c997' },
            { label: 'Strong', color: '#198754' }
        ];

        return strengths[Math.min(strength - 1, 4)] || { strength: 0, label: '', color: '' };
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.password.trim()) {
            newErrors.password = "Password is required";
        } else if (formData.password.length < 6) {
            newErrors.password = "Password must be at least 6 characters long";
        }

        if (!formData.confirmPassword.trim()) {
            newErrors.confirmPassword = "Please confirm your password";
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = "Passwords do not match";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle input changes
    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    // Submit reset password
    const resetPassword = async () => {
        if (!validateForm()) return;

        setLoading(true);
        
        try {
            const submitData = new FormData();
            submitData.append('mobile', mobile);
            submitData.append('password', formData.password);

            const response = await axios.post(
                'https://octafxx.in/Rest/web_reset_password_api/', 
                submitData,
                { timeout: 30000 }
            );

            if (response.data.status === 0) {
                alert("Password reset successfully! You can now login with your new password.");
                navigate('/login');
            } else {
                alert(response.data.msg || "Failed to reset password. Please try again.");
            }
        } catch (error) {
            console.error('Password reset error:', error);
            if (error.response) {
                alert(error.response.data.msg || "Failed to reset password. Please try again.");
            } else if (error.request) {
                alert("Network error. Please check your connection and try again.");
            } else {
                alert("An unexpected error occurred. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    // Password requirements checklist
    const passwordRequirements = [
        { id: 1, text: 'At least 6 characters long', met: formData.password.length >= 6 },
        { id: 2, text: 'Contains uppercase letter', met: /[A-Z]/.test(formData.password) },
        { id: 3, text: 'Contains lowercase letter', met: /[a-z]/.test(formData.password) },
        { id: 4, text: 'Contains number', met: /[0-9]/.test(formData.password) },
        { id: 5, text: 'Contains special character', met: /[^A-Za-z0-9]/.test(formData.password) }
    ];

    const passwordStrength = getPasswordStrength(formData.password);

    return (
        <div className="min-vh-100 bg-gradient-primary">
            <Helmet>
                <title>Set New Password | AUROFX MARKET</title>
                <meta name="description" content="Set your new password for AUROFX MARKET account" />
            </Helmet>

            {/* Header */}
            <div className="bg-transparent">
                <div className="container-fluid py-4">
                    <div className="row align-items-center">
                        <div className="col-auto">
                            <Link to="/forgotpassword" className="btn btn-outline-light btn-sm">
                                <i className="fas fa-arrow-left me-2"></i>
                                Back
                            </Link>
                        </div>
                        <div className="col text-center">
                            <h4 className="mb-0 text-white fw-bold">Set New Password</h4>
                        </div>
                        <div className="col-auto">
                            <div className="text-white opacity-75">
                                <small>Secure your account</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid pb-5">
                <div className="row justify-content-center">
                    <div className="col-12 col-md-8 col-lg-6">
                        {/* Loading Overlay */}
                        {loading && (
                            <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-50" style={{zIndex: 9999}}>
                                <div className="text-center">
                                    <div className="spinner-border text-light mb-3" style={{width: '3rem', height: '3rem'}}>
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="text-white mb-0">Updating your password...</p>
                                </div>
                            </div>
                        )}

                        <div className="card border-0 shadow-lg">
                            <div className="card-body p-4 p-md-5">
                                <div className="text-center mb-4">
                                    <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                                         style={{width: '80px', height: '80px'}}>
                                        <i className="fas fa-key fa-2x text-success"></i>
                                    </div>
                                    <h2 className="fw-bold text-dark mb-2">Create New Password</h2>
                                    <p className="text-muted">
                                        Enter a strong password to secure your account
                                    </p>
                                </div>

                                {/* Mobile Number Display */}
                                {mobile && (
                                    <div className="alert alert-info border-0 mb-4">
                                        <div className="d-flex align-items-center">
                                            <i className="fas fa-mobile-alt me-2"></i>
                                            <div>
                                                <strong>Verifying for:</strong> {mobile}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="row g-3">
                                    {/* New Password Field */}
                                    <div className="col-12">
                                        <label className="form-label fw-semibold">
                                            <i className="fas fa-lock text-primary me-2"></i>
                                            New Password *
                                        </label>
                                        <div className="password-input-group">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                className={`form-control form-control-lg ${errors.password ? 'is-invalid' : ''}`}
                                                value={formData.password}
                                                onChange={(e) => handleInputChange('password', e.target.value)}
                                                placeholder="Enter new password"
                                                disabled={loading}
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle-btn"
                                                onClick={() => setShowPassword(!showPassword)}
                                                disabled={loading}
                                            >
                                                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                            </button>
                                        </div>
                                        {errors.password && (
                                            <div className="invalid-feedback d-block">
                                                {errors.password}
                                            </div>
                                        )}

                                        {/* Password Strength Indicator */}
                                        {formData.password && (
                                            <div className="mt-2">
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <small className="text-muted">Password Strength:</small>
                                                    <small style={{ color: passwordStrength.color, fontWeight: '600' }}>
                                                        {passwordStrength.label}
                                                    </small>
                                                </div>
                                                <div className="password-strength-bar">
                                                    <div 
                                                        className="password-strength-progress"
                                                        style={{
                                                            width: `${(passwordStrength.strength / 5) * 100}%`,
                                                            backgroundColor: passwordStrength.color
                                                        }}
                                                    ></div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Confirm Password Field */}
                                    <div className="col-12">
                                        <label className="form-label fw-semibold">
                                            <i className="fas fa-lock text-primary me-2"></i>
                                            Confirm New Password *
                                        </label>
                                        <div className="password-input-group">
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                className={`form-control form-control-lg ${errors.confirmPassword ? 'is-invalid' : ''}`}
                                                value={formData.confirmPassword}
                                                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                                                placeholder="Confirm your new password"
                                                disabled={loading}
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle-btn"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                disabled={loading}
                                            >
                                                <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                            </button>
                                        </div>
                                        {errors.confirmPassword && (
                                            <div className="invalid-feedback d-block">
                                                {errors.confirmPassword}
                                            </div>
                                        )}
                                    </div>

                                    {/* Password Requirements */}
                                    <div className="col-12">
                                        <div className="password-requirements">
                                            <h6 className="fw-semibold mb-3">Password Requirements:</h6>
                                            <div className="requirements-list">
                                                {passwordRequirements.map(req => (
                                                    <div key={req.id} className="requirement-item">
                                                        <i 
                                                            className={`fas ${req.met ? 'fa-check-circle text-success' : 'fa-times-circle text-muted'} me-2`}
                                                        ></i>
                                                        <span className={req.met ? 'text-success' : 'text-muted'}>
                                                            {req.text}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Submit Button */}
                                    <div className="col-12">
                                        <button
                                            onClick={resetPassword}
                                            className="btn btn-success btn-lg w-100 py-3 fw-bold"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <div className="spinner-border spinner-border-sm me-2" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    UPDATING PASSWORD...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-check-circle me-2"></i>
                                                    SET NEW PASSWORD
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Security Tips */}
                                <div className="mt-4 pt-3 border-top">
                                    <div className="alert alert-warning border-0">
                                        <div className="d-flex align-items-start">
                                            <i className="fas fa-shield-alt me-2 mt-1"></i>
                                            <div>
                                                <strong>Security Tips:</strong>
                                                <ul className="mb-0 mt-1 small">
                                                    <li>Use a unique password that you don't use elsewhere</li>
                                                    <li>Avoid common words and personal information</li>
                                                    <li>Consider using a password manager</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Back to Login */}
                                <div className="text-center mt-4">
                                    <p className="text-muted mb-0">
                                        Remember your password?{' '}
                                        <Link to="/login" className="text-primary fw-semibold text-decoration-none">
                                            Back to Login
                                        </Link>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}